import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card, Button } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi } from '../services/api';
import { KPI_REGISTRY, KPI_CATEGORIES } from '../constants/kpiRegistry';

export function AdminKpiDefaults() {
  const { t } = useTranslation();
  const [enabledKpis, setEnabledKpis] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadDefaults();
  }, []);

  const loadDefaults = async () => {
    try {
      const data = await adminApi.getKpiDefaults();
      setEnabledKpis(data.enabledKpis || []);
    } catch (error) {
      console.error('Failed to load KPI defaults:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggle = (id: string) => {
    setEnabledKpis(prev => prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      await adminApi.updateKpiDefaults(enabledKpis);
      setSaveMessage({ type: 'success', text: 'KPI-standardinställningar sparade' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch {
      setSaveMessage({ type: 'error', text: 'Kunde inte spara' });
    } finally {
      setIsSaving(false);
    }
  };

  const categories = Object.entries(KPI_CATEGORIES);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="text-center py-5"><div className="spinner-border" role="status" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Breadcrumb items={[
        { label: t('admin.nav.system', 'System'), path: '/admin/system-menu' },
        { label: 'KPI-standardinställningar' }
      ]} />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">KPI-standardinställningar</h4>
        <div className="d-flex align-items-center gap-2">
          {saveMessage && (
            <span className={`text-${saveMessage.type === 'success' ? 'success' : 'danger'} small`}>
              {saveMessage.text}
            </span>
          )}
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Sparar...' : 'Spara'}
          </Button>
        </div>
      </div>

      <Card className="mb-4">
        <p className="text-muted small mb-3">
          Välj vilka nyckeltal som ska visas som standard på nya företags startsida, i den ordning du väljer dem. Företagen kan sedan själva anpassa sina val.
        </p>
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="badge bg-primary">{enabledKpis.length} valda</span>
          <span className="badge bg-light text-dark border">{KPI_REGISTRY.length} tillgängliga</span>
        </div>
      </Card>

      {categories.map(([catKey, cat]) => {
        const kpis = KPI_REGISTRY.filter(k => k.category === catKey);
        const selectedInCat = kpis.filter(k => enabledKpis.includes(k.id)).length;

        return (
          <Card key={catKey} className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center gap-2">
                <i className={`feather-${cat.icon} text-primary`}></i>
                <h6 className="mb-0">{cat.label_sv}</h6>
              </div>
              <span className="badge bg-light text-dark border">{selectedInCat} / {kpis.length}</span>
            </div>

            <div className="row g-2">
              {kpis.map(kpi => {
                const isSelected = enabledKpis.includes(kpi.id);
                return (
                  <div key={kpi.id} className="col-md-6">
                    <div
                      className={`d-flex align-items-center gap-3 p-3 rounded border ${isSelected ? 'border-primary' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggle(kpi.id)}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input flex-shrink-0"
                        checked={isSelected}
                        onChange={() => toggle(kpi.id)}
                        onClick={e => e.stopPropagation()}
                      />
                      <div>
                        <div className="fw-semibold" style={{ fontSize: 14 }}>
                          <i className={`feather-${kpi.icon} me-1`} style={{ fontSize: 13 }}></i>
                          {kpi.label_sv}
                        </div>
                        <small className="text-muted">{kpi.description_sv}</small>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}
    </DashboardLayout>
  );
}
